import {
  ComposableServiceConfigPaymentMode,
  Coupon,
  NestedCustomerSatisfaction,
  ReservableServiceSlot,
  ServiceTimeSlotReservationOptions,
  ServiceType,
  ServiceTypeListItemType,
  User,
} from ".";
import { IBookingReservationFields } from "./Booking";

export interface ServiceReservationLinkedAmenityReservationService {
  id: string;
  name: string;
}

export interface ServiceReservationLinkedAmenityReservationServiceSlot {
  key: string;
  fromTime: number;
  toTime: number;
  durationInMinutes: number;
}

export interface ServiceReservationLinkedAmenityReservation {
  id: string;
  amenityDefinitionId: string;
  code: string;
  date: Date;
  startDate: Date;
  service: ServiceReservationLinkedAmenityReservationService;
  slots: ServiceReservationLinkedAmenityReservationServiceSlot[];
}

export interface ServiceReservationNestedService {
  id: string;
  name: string;
  providedBy: {
    id: string;
    name: string;
  };
  type: ServiceType;
  paymentMode?: ComposableServiceConfigPaymentMode;
}

export interface ServiceReservationListItem {
  id: string;
  label: string;
  price: number;
  categories: string[];
  type: ServiceTypeListItemType;
  quantity?: number;
}

export interface ServiceReservationSlot {
  key: string;
  fromTime: number;
  toTime: number;
  durationInMinutes: number;
  reservationOptions?: ServiceTimeSlotReservationOptions[];
}

export interface ServiceReservationBuilding {
  id: string;
  code: string;
  name: string;
}

export interface ServiceReservationApartment {
  id: string;
  code: string;
}

export interface ServiceReservationUser {
  id: string;
  name: string;
}

export enum ServiceReservationStatus {
  PENDING = "pending",
  PAYMENT_PENDING = "payment_pending",
  REQUESTED_PAYMENT = "requested_payment",
  WAITING_FOR_PROVIDER_CONFIRM = "waiting_for_provider_confirm",
  WAITING_FOR_CAPTURE = "waiting_for_capture",
  CONFIRMED = "confirmed",
}

export enum ServiceReservationExternalClientReservationDataPaymentStatus {
  UNPAID = "unpaid",
  PAYMENT_FAILED = "payment_failed",
  PAID = "paid",
}

export interface ServiceReservationExternalClientReservationDataDetail {
  orderCode?: string;
  xAuthToken?: string;
  estimatedDeliveryDate?: number;
  estimatedPickupDate?: number;
  pickedItems: string[];
  totalPrice?: number;
  paymentStatus: ServiceReservationExternalClientReservationDataPaymentStatus;
}

export interface ServiceReservationExternalClientReservationData {
  details?: ServiceReservationExternalClientReservationDataDetail[];
  rawOrders?: any[];
}

export interface ServiceReservationVehicle {
  id: string;
  name?: string;
  model: string;
  plate: string;
  box?: string;
}

export interface ServiceReservation extends IBookingReservationFields {
  id?: string;
  code?: string;
  date: number;
  startDate: number;
  building: ServiceReservationBuilding;
  apartment?: ServiceReservationApartment;
  service: ServiceReservationNestedService;
  serviceProvidedBy?: ServiceReservationUser;
  serviceTypeListItems: ServiceReservationListItem[];
  totalPrice: number;
  /**
   * Holds the price effectively captured from the payment intent in case of
   * pre-authorization
   */
  rectifiedPrice?: number;
  status: ServiceReservationStatus;
  slots: ServiceReservationSlot[];
  deletionDate?: number;
  createdBy: ServiceReservationUser;
  targetUser: ServiceReservationUser;
  userDetails?: User;
  deletedBy?: ServiceReservationUser;
  additionalDetails?: string;
  vehicle?: ServiceReservationVehicle;
  invoiceRequested?: boolean;
  linkedAmenityReservation?: ServiceReservationLinkedAmenityReservation;
  /**
   * Holds reservation informations retrieved from external APIs.
   * This is not meant to be a modeled field because such data should never, ever
   * be needed.
   */
  externalClientReservationData?: ServiceReservationExternalClientReservationData;
  customerSatisfaction?: NestedCustomerSatisfaction;
  pushMessage?: string;
  lastTimeForPayment?: Date;
  survey?: ServiceReservationSurveyAnswer[];
  orderCoupon?: Coupon;
  isQuotation?: boolean;
}

export interface ServiceReservationSurveyAnswer {
  question: string;
  answer?: string;
}

export type ServiceGroupedReservationSlot = {
  reservable: boolean;
  slots: ServiceReservationReservableSlot[];
  /**
   * This holds the amenity slots that needs to be reserved with the above
   * slots for the desired place.
   *
   * A "reverse" check is done in the `reserveService` routine where we double
   * check if the specified slots are effectively reservable.
   */
  amenitySlots?: ReservableServiceSlot[];
  fromTime: number;
  toTime: number;
};

// Custom type used to give feedback to the client and to take input from it.
// Basically, it's a model used to compute and retrieve informations.
export interface ServiceReservationReservableSlot
  extends ServiceReservationSlot {
  reservationTimeOffsetInHours?: number;
  date: Date;
  serviceId: string;
  reservable?: boolean;
  maxUnits?: number;
}
